'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { chipColors } from '@/styles/theme/colors';
import { palette, styled } from '@mui/system';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { useSettings } from '@/hooks/use-settings';
import { visuallyHidden } from '@mui/utils';
import { logger } from '@/lib/default-logger';

// https://mui.com/material-ui/react-table/#customization
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.background,
  },
  '&:nth-of-type(even)': {
    backgroundColor: "var(--mui-palette-background-level1)",
  },
}));

function descendingComparator(a, b, orderBy) {
  // logger.debug('a, b', a[orderBy], b[orderBy]);
  let valueToSort = null;
  if (a[orderBy]?.dis || b[orderBy]?.dis) {
    valueToSort = 'dis';
  } else if (a[orderBy]?.value || b[orderBy]?.value) {
    valueToSort = 'value';
  } else if (a[orderBy]?.val || b[orderBy]?.val) {
    valueToSort = 'val';
  }

  if (valueToSort) {
    if (!a[orderBy] || (b[orderBy] && b[orderBy][valueToSort] < a[orderBy][valueToSort])) {
        return -1;
      }
    if (!b[orderBy] || (a[orderBy] && b[orderBy][valueToSort] > a[orderBy][valueToSort])) {
        return 1;
    }
  } else {
    if (!a[orderBy] || (b[orderBy] && b[orderBy] < a[orderBy])) {
        return -1;
      }
    if (!b[orderBy] || (a[orderBy] && b[orderBy] > a[orderBy])) {
        return 1;
    }
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator, order, orderBy) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  // logger.debug('stable this', stabilizedThis, array);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    // logger.debug('stable', a, b, order);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);

  // let sorted;
  // if (order === 'asc') {
  //   sorted = array.sort((a, b) => {a[orderBy]?.value - b[orderBy]?.value});
  // } else sorted = array.sort((a, b) => {b[orderBy]?.value - a[orderBy]?.value});

  // return sorted;

}

function EnhancedTableHead(props) {
  const { columns, selectable, hideHead, onSelectAll, onDeselectAll, order, orderBy, numSelected, rowCount, onRequestSort, mode, selectedAll, selectedSome, selected } = props;

  // const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  // logger.debug('columns', columns);

  const activeSortColor = mode === 'light' ? 'var(--mui-palette-neutral-900)' : 'white'

  const inactiveSortColor = mode === 'light' ? 'var(--mui-palette-neutral-500)' : 'var(--mui-palette-neutral-400)'

  // logger.debug('active color', activeSortColor, mode);

  return (
    <TableHead sx={{ ...(hideHead && { visibility: 'collapse', '--TableCell-borderWidth': 0 }) }}>
      <TableRow>
        {selectable ? (
          <TableCell padding="checkbox" sx={{ width: '40px', minWidth: '40px', maxWidth: '40px' }}>
            <Checkbox
              checked={selectedAll}
              // indeterminate={selectedSome}
              onChange={(event) => {
                if (selectedAll) {
                  onDeselectAll?.(event);
                } else {
                  onSelectAll?.(event);
                }
                // logger.debug('select all', event?.target?.checked, selectedAll, selectedSome);

                // if (event.target.checked) {
                //   onSelectAll?.(event);
                // } else {
                //   onDeselectAll?.(event);
                // }
              }}
            />
          </TableCell>
          ) : null}
          {columns?.map((column) => (
            <TableCell
              key={column.name}
              sx={{
                width: column.width,
                minWidth: column.width,
                maxWidth: column.width,
                ...(column.align && { textAlign: column.align }),
              }}
              sortDirection={orderBy === column.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === column.field}
                direction={orderBy === column.field ? order : 'asc'}
                onClick={createSortHandler(column.field)}
                hideSortIcon={false}
                sx={{
                  '.MuiTableSortLabel-icon': {
                    opacity: 1,
                    // color: orderBy === column.field ? `${chipColors['blue'][500]} !important` : 'text.secondary'
                    // color: orderBy !== column.field ? 'text.secondary' : `${theme.palette.text.primary} !important`
                    // color: orderBy === column.field ? activeSortColor : inactiveSortColor
                    color: `${orderBy === column.field ? activeSortColor : inactiveSortColor} !important`
                  },
                  // '& .Mui-active': {
                  //   // color: 'blue'
                  //   '& .MuiTableSortLabel-icon': {
                  //     color: 'blue'
                  //   }
                  // }
                  // ['.Mui-active' > '.MuiTableSortLabel-icon']: {
                  //     color: 'blue'
                  //   }

                }}
              >
                {column.hideName ? null : column.name}
                {orderBy === column.field ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>  
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

export function DataTable({
  columns,
  hideHead,
  hover,
  onClick,
  onDeselectAll,
  onDeselectOne,
  onSelectOne,
  onSelectAll,
  rows,
  selectable,
  selected,
  uniqueRowId,
  ...props
}) {
  // const selectedSome = (selected?.size ?? 0) > 0 && (selected?.size ?? 0) < rows.length;
  // const selectedAll = rows.length > 0 && selected?.size === rows.length;
  const selectedSome = selected?.size < rows?.length;
  const selectedAll = selected?.size === rows?.length;
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(null);
  const [sortedRows, setSortedRows] = React.useState([]);
  const { settings } = useSettings();

  // logger.debug('data table', selected, selected?.size, selectedAll, selectedSome);

  const handleRequestSort = (event, property) => {
    // logger.debug('new order', property, orderBy, order);
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  React.useEffect(() => {
    // logger.debug('new order', orderBy, order);
  }, [order, orderBy]);

  // const sortedRows = React.useMemo(
  //   // () =>
  //   //   stableSort(rows, getComparator(order, orderBy)).slice(
  //   //     page * rowsPerPage,
  //   //     page * rowsPerPage + rowsPerPage,
  //   //   ),
  //   // [order, orderBy, page, rowsPerPage],
  //   () => {
  //     // return (
  //       if (orderBy !== null && orderBy !== undefined){
  //         return stableSort(rows, getComparator(order, orderBy))
  //       } else return rows;
  //     // )
  //   },
      
  //   [order, orderBy],
  // );

  React.useEffect(() => {
    if (orderBy !== null && orderBy !== undefined){
      setSortedRows(stableSort(rows, getComparator(order, orderBy), order, orderBy))
    } else setSortedRows(rows);
    // logger.debug('sorted rows', sortedRows, rows, order, orderBy);
  }, [order, orderBy, rows]);

  // logger.debug('sorted rows', sortedRows, rows, order, orderBy);

  return (
    <Table {...props}>
      {/* <TableHead sx={{ ...(hideHead && { visibility: 'collapse', '--TableCell-borderWidth': 0 }) }}>
        <TableRow>
          {selectable ? (
            <TableCell padding="checkbox" sx={{ width: '40px', minWidth: '40px', maxWidth: '40px' }}>
              <Checkbox
                checked={selectedAll}
                indeterminate={selectedSome}
                onChange={(event) => {
                  if (selectedAll) {
                    onDeselectAll?.(event);
                  } else {
                    onSelectAll?.(event);
                  }
                }}
              />
            </TableCell>
          ) : null}
          {columns.map((column) => (
            <TableCell
              key={column.name}
              sx={{
                width: column.width,
                minWidth: column.width,
                maxWidth: column.width,
                ...(column.align && { textAlign: column.align }),
              }}
            >
              {column.hideName ? null : column.name}
            </TableCell>
          ))}
        </TableRow>
      </TableHead> */}
      <EnhancedTableHead
        numSelected={selected?.length}
        columns={columns}
        selectable={selectable}
        hideHead={hideHead}
        order={order}
        orderBy={orderBy}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        onRequestSort={handleRequestSort}
        rowCount={rows?.length}
        mode={settings?.colorScheme}
        selectedAll={selectedAll}
        selectedSome={selectedSome}
        selected={selected}
      />
      <TableBody {...props}>
        {sortedRows?.map((row, index) => {
          const rowId = row.id?.value || row.id || uniqueRowId?.(row);
          const rowSelected = rowId && Object.keys(selected)?.length > 0 ? selected?.has(rowId) : false;
          // const rowSelected = rowId && selected?.length > 0 ? selected?.find((entry) => entry === rowId) : false;
          // const rowSelected = selected?.find((entry) => entry === rowId);
          // let rowSelected = false;
          // if (selected.length > 0) {
          //   if (selected[0].value) {
          //     let matchFound = selected?.find((entry) => entry.value === rowId)
          //     if (matchFound) rowSelected = true;
          //   } else if (selected[0].id?.val) {
          //     let matchFound = selected?.find((entry) => entry.id.val === rowId)
          //     if (matchFound) rowSelected = true;
              
          //   }
          // }

          // const [rowSelected, setRowSelected] = React.useState(false);
          // let rowSelected = false;
          const bgColor = index % 2 === 0 ? 'white' : 'gray';
          // logger.debug('row bg:', bgColor, rowId, rowSelected, row, selected);

          return (
            <StyledTableRow
              hover={hover}
              key={rowId ?? index}
              rowselected={rowSelected.toString()}
              selected={selected}
              rowid={rowId}
              {...(onClick && {
                onClick: (event) => {
                  onClick(event, row);
                },
              })}
              sx={{ ...(onClick && { cursor: 'pointer' })}}
            >
              {selectable ? (
                <TableCell padding="checkbox">
                  <Checkbox
                    // checked={rowId ? rowSelected : false}
                    // checked={rowSelected}
                    checked={selected?.has(rowId)}
                    onChange={(event) => {
                      
                      if (event?.target?.checked) {
                        onSelectOne?.(event, row);
                      } else {
                        onDeselectOne?.(event, row);
                      }
                      // onSelectOne?.(event, row);
                      // let isSelected = !rowSelected;
                      // if (isSelected) {
                        // selected.push(row);
                      // }
                      // rowSelected = isSelected;

                      // logger.debug('row select', event?.target?.checked, rowSelected, rowId, selected);
                    }}
                    onClick={(event) => {
                      if (onClick) {
                        event?.stopPropagation();
                      }
                    }}
                  />
                </TableCell>
              ) : null}
              {columns?.map((column) => (
                <TableCell key={column.name} sx={{ ...(column.align && { textAlign: column.align })}}>
                  {column.formatter ? column.formatter(row, index, column) : column.field ? row[column.field] : null}
                </TableCell>
              ))}
            </StyledTableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
